import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Mail, Phone, Plus, Trash2, Send, Check, X, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";


export default function NotificationSettings() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);
  const [formData, setFormData] = useState({
    contact_type: "email",
    contact_value: "",
    name: "",
    notify_on_rsvp: true,
    notify_on_guest_confirm: true,
    notify_on_gift: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          if (siteData) {
            setSite(siteData);
            const contactsRes = await fetch(`${API}/sites/${siteData.site_id}/notification-contacts`, { credentials: 'include' });
            if (contactsRes.ok) {
              const contactsData = await contactsRes.json();
              setContacts(contactsData);
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const resetForm = () => {
    setFormData({
      contact_type: "email",
      contact_value: "",
      name: "",
      notify_on_rsvp: true,
      notify_on_guest_confirm: true,
      notify_on_gift: true
    });
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_value) {
      toast.error("Preencha o email ou telefone");
      return;
    }

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/notification-contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const contact = await res.json();
        setContacts(prev => [...prev, contact]);
        toast.success("Contato adicionado!");
        setDialogOpen(false);
        resetForm();
      } else {
        throw new Error('Erro ao adicionar');
      }
    } catch (error) {
      toast.error("Erro ao adicionar contato");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Remover este contato de notificação?")) return;

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/notification-contacts/${contactId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setContacts(prev => prev.filter(c => c.contact_id !== contactId));
        toast.success("Contato removido");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erro ao remover contato");
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Erro ao remover contato");
    }
  };

  const handleToggleContact = async (contact) => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/notification-contacts/${contact.contact_id}/toggle`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setContacts(prev => prev.map(c => 
          c.contact_id === contact.contact_id ? { ...c, is_active: data.is_active } : c
        ));
        toast.success(data.is_active ? "Notificações ativadas" : "Notificações desativadas");
      }
    } catch (error) {
      toast.error("Erro ao atualizar contato");
    }
  };

  const handleTestNotification = async () => {
    setTestingNotification(true);
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/test-notification`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();
      
      if (res.ok) {
        if (data.simulated) {
          toast.info(data.message);
        } else {
          toast.success(data.message);
        }
      } else {
        toast.error(data.detail || "Erro ao enviar teste");
      }
    } catch (error) {
      toast.error("Erro ao enviar teste");
    } finally {
      setTestingNotification(false);
    }
  };

  const activeEmailContacts = contacts.filter(c => c.contact_type === "email" && c.is_active);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="notification-settings">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/guest-list')}
              className="text-[#6E0F1A]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Bell className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Notificações</span>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full" data-testid="add-contact-btn">
                <Plus className="w-4 h-4 mr-2" />
                Adicionar
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl text-[#6E0F1A]">
                  Adicionar Contato
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddContact} className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#6E0F1A]">Tipo de Contato</Label>
                  <Select 
                    value={formData.contact_type} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, contact_type: value, contact_value: "" }))}
                  >
                    <SelectTrigger className="input-wedding mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </div>
                      </SelectItem>
                      <SelectItem value="whatsapp">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          WhatsApp (em breve)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="name" className="text-[#6E0F1A]">Nome (opcional)</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Para identificar este contato"
                    className="input-wedding mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_value" className="text-[#6E0F1A]">
                    {formData.contact_type === "email" ? "Email *" : "Telefone *"}
                  </Label>
                  <Input
                    id="contact_value"
                    type={formData.contact_type === "email" ? "email" : "tel"}
                    value={formData.contact_value}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_value: e.target.value }))}
                    placeholder={formData.contact_type === "email" ? "email@exemplo.com" : "+55 11 99999-9999"}
                    className="input-wedding mt-2"
                    required
                  />
                </div>

                <div className="space-y-3 pt-4 border-t border-[#F3C9CF]/30">
                  <p className="text-sm font-medium text-[#6E0F1A]">Notificar quando:</p>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="notify_rsvp" className="text-[#6E0F1A] cursor-pointer">
                      Confirmação via RSVP
                    </Label>
                    <Switch
                      id="notify_rsvp"
                      checked={formData.notify_on_rsvp}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_on_rsvp: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="notify_guest" className="text-[#6E0F1A] cursor-pointer">
                      Convidado confirma da lista
                    </Label>
                    <Switch
                      id="notify_guest"
                      checked={formData.notify_on_guest_confirm}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_on_guest_confirm: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="notify_gift" className="text-[#6E0F1A] cursor-pointer">
                      Presente comprado
                    </Label>
                    <Switch
                      id="notify_gift"
                      checked={formData.notify_on_gift}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_on_gift: checked }))}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6"
                >
                  Adicionar Contato
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Info Card */}
        <Card className="card-dashboard mb-6 bg-[#567D50]/5 border-[#567D50]/20">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-[#567D50] mt-0.5" />
              <div>
                <p className="text-sm text-[#6E0F1A] font-medium">Receba avisos em tempo real</p>
                <p className="text-xs text-[#6E0F1A]/70 mt-1">
                  Cadastre emails para receber notificações quando convidados confirmarem presença ou comprarem presentes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Notification */}
        {activeEmailContacts.length > 0 && (
          <Card className="card-dashboard mb-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#6E0F1A]">Testar Notificações</p>
                  <p className="text-xs text-[#6E0F1A]/60">
                    Envia um email de teste para {activeEmailContacts.length} contato(s) ativo(s)
                  </p>
                </div>
                <Button
                  onClick={handleTestNotification}
                  disabled={testingNotification}
                  variant="outline"
                  className="border-[#B3121B] text-[#B3121B]"
                  data-testid="test-notification-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {testingNotification ? "Enviando..." : "Enviar Teste"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts List */}
        {contacts.length === 0 ? (
          <Card className="card-dashboard text-center py-16">
            <CardContent>
              <div className="w-20 h-20 bg-[#F3C9CF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Bell className="w-10 h-10 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-3">
                Nenhum contato cadastrado
              </h2>
              <p className="text-[#6E0F1A]/70 mb-6">
                Adicione emails para receber notificações sobre confirmações e presentes
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact) => (
              <Card key={contact.contact_id} className="card-dashboard" data-testid={`contact-${contact.contact_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${contact.is_active ? 'bg-[#567D50]/10' : 'bg-gray-100'}`}>
                        {contact.contact_type === "email" ? (
                          <Mail className={`w-5 h-5 ${contact.is_active ? 'text-[#567D50]' : 'text-gray-400'}`} />
                        ) : (
                          <Phone className={`w-5 h-5 ${contact.is_active ? 'text-[#567D50]' : 'text-gray-400'}`} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#6E0F1A]">
                            {contact.name || contact.contact_value}
                          </p>
                          {!contact.is_active && (
                            <Badge variant="secondary" className="bg-gray-100 text-gray-500">
                              Inativo
                            </Badge>
                          )}
                        </div>
                        {contact.name && (
                          <p className="text-sm text-[#6E0F1A]/60">{contact.contact_value}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {contact.notify_on_rsvp && (
                            <span className="text-xs bg-[#F3C9CF]/30 text-[#6E0F1A]/70 px-2 py-0.5 rounded">RSVP</span>
                          )}
                          {contact.notify_on_guest_confirm && (
                            <span className="text-xs bg-[#F3C9CF]/30 text-[#6E0F1A]/70 px-2 py-0.5 rounded">Lista</span>
                          )}
                          {contact.notify_on_gift && (
                            <span className="text-xs bg-[#F3C9CF]/30 text-[#6E0F1A]/70 px-2 py-0.5 rounded">Presentes</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={contact.is_active}
                        onCheckedChange={() => handleToggleContact(contact)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteContact(contact.contact_id)}
                        className="text-[#6E0F1A]/60 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* SendGrid Notice */}
        <Card className="card-dashboard mt-8 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Sobre as notificações</p>
                <p className="text-xs text-blue-700 mt-1">
                  As notificações serão enviadas automaticamente quando convidados confirmarem presença ou comprarem presentes.
                  O sistema registra os contatos e os eventos de notificação.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
